import type { TFunction } from 'i18next';
import { fromDb } from './loadStatus';

/**
 * The team activity feed (change spec 2026-10-02/activity-feed, design 5 and 9.2): the row shape of
 * `activity_feed`, the label of each kind, where a row links to, and which kind groups a reader is
 * offered. Pure, so the mapping is tested without a screen.
 *
 * `ACTIVITY_KINDS` mirrors the seed of `app.activity_kinds` (0265). `scripts/check-activity-kinds.mjs`
 * compares the three lists (what the migrations emit, the seed, this one): a kind added to a writer
 * without its classification and its label here fails that check. A kind this portal does not know
 * still renders, as "Other activity".
 */

export const ACTIVITY_GROUPS = [
  'load_lifecycle',
  'load_changes',
  'load_tracking',
  'documents',
  'receivables',
  'fleet',
  'brokers',
  'team',
  'agency',
  'money',
] as const;
export type ActivityGroup = (typeof ACTIVITY_GROUPS)[number];
/** A row whose kind no migration classified yet (the admin only sees it). */
export type ActivityGroupOrOther = ActivityGroup | 'other';

/** The groups that hold only office rows (no carrier): offered with `activity.office` only. */
export const OFFICE_GROUPS: readonly ActivityGroup[] = ['brokers', 'team', 'agency'];

export type ActivitySource = 'load_events' | 'activity_log';

export const ACTIVITY_KINDS: readonly { source: ActivitySource; kind: string; group: ActivityGroup }[] = [
  { source: 'load_events', kind: 'document_added', group: 'documents' },
  { source: 'load_events', kind: 'document_archived', group: 'documents' },
  { source: 'load_events', kind: 'document_invoice_flag', group: 'documents' },
  { source: 'load_events', kind: 'document_replaced', group: 'documents' },
  { source: 'load_events', kind: 'repriced', group: 'load_changes' },
  { source: 'load_events', kind: 'stop_added', group: 'load_changes' },
  { source: 'load_events', kind: 'stop_removed', group: 'load_changes' },
  { source: 'load_events', kind: 'stop_rescheduled', group: 'load_changes' },
  { source: 'load_events', kind: 'updated', group: 'load_changes' },
  { source: 'load_events', kind: 'created', group: 'load_lifecycle' },
  { source: 'load_events', kind: 'transition', group: 'load_lifecycle' },
  { source: 'load_events', kind: 'stop_arrived', group: 'load_tracking' },
  { source: 'load_events', kind: 'stop_departed', group: 'load_tracking' },
  { source: 'load_events', kind: 'fee_reversed', group: 'money' },
  { source: 'load_events', kind: 'fee_dispute_cleared', group: 'receivables' },
  { source: 'load_events', kind: 'fee_disputed', group: 'receivables' },
  { source: 'load_events', kind: 'quick_pay_requested', group: 'receivables' },
  { source: 'activity_log', kind: 'agency.closed', group: 'agency' },
  { source: 'activity_log', kind: 'agency.created', group: 'agency' },
  { source: 'activity_log', kind: 'agency.document_requirement_set', group: 'agency' },
  { source: 'activity_log', kind: 'agency.flag_set', group: 'agency' },
  { source: 'activity_log', kind: 'agency.profile_updated', group: 'agency' },
  { source: 'activity_log', kind: 'agency.reactivated', group: 'agency' },
  { source: 'activity_log', kind: 'agency.settings_updated', group: 'agency' },
  { source: 'activity_log', kind: 'agency.suspended', group: 'agency' },
  { source: 'activity_log', kind: 'broker_credit.checked', group: 'brokers' },
  { source: 'activity_log', kind: 'broker_credit.fmcsa_lookup', group: 'brokers' },
  { source: 'activity_log', kind: 'broker_credit.overridden', group: 'brokers' },
  { source: 'activity_log', kind: 'broker_incident.recorded', group: 'brokers' },
  { source: 'activity_log', kind: 'broker_incident.reopened', group: 'brokers' },
  { source: 'activity_log', kind: 'broker_incident.resolved', group: 'brokers' },
  { source: 'activity_log', kind: 'broker_setup.created', group: 'brokers' },
  { source: 'activity_log', kind: 'broker_setup.updated', group: 'brokers' },
  { source: 'activity_log', kind: 'broker.created', group: 'brokers' },
  { source: 'activity_log', kind: 'broker.updated', group: 'brokers' },
  { source: 'activity_log', kind: 'factoring.created', group: 'brokers' },
  { source: 'activity_log', kind: 'factoring.deactivated', group: 'brokers' },
  { source: 'activity_log', kind: 'factoring.updated', group: 'brokers' },
  { source: 'activity_log', kind: 'document.added', group: 'documents' },
  { source: 'activity_log', kind: 'document.archived', group: 'documents' },
  { source: 'activity_log', kind: 'document.dates_set', group: 'documents' },
  { source: 'activity_log', kind: 'document.replaced', group: 'documents' },
  { source: 'activity_log', kind: 'carrier.created', group: 'fleet' },
  { source: 'activity_log', kind: 'carrier.status_changed', group: 'fleet' },
  { source: 'activity_log', kind: 'carrier.updated', group: 'fleet' },
  { source: 'activity_log', kind: 'driver.account_deleted', group: 'fleet' },
  { source: 'activity_log', kind: 'driver.created', group: 'fleet' },
  { source: 'activity_log', kind: 'driver.invite_created', group: 'fleet' },
  { source: 'activity_log', kind: 'driver.invite_revoked', group: 'fleet' },
  { source: 'activity_log', kind: 'driver.linked', group: 'fleet' },
  { source: 'activity_log', kind: 'driver.seat_added', group: 'fleet' },
  { source: 'activity_log', kind: 'driver.seat_ended', group: 'fleet' },
  { source: 'activity_log', kind: 'driver.status_changed', group: 'fleet' },
  { source: 'activity_log', kind: 'driver.updated', group: 'fleet' },
  { source: 'activity_log', kind: 'owner.created', group: 'fleet' },
  { source: 'activity_log', kind: 'owner.status_changed', group: 'fleet' },
  { source: 'activity_log', kind: 'owner.updated', group: 'fleet' },
  { source: 'activity_log', kind: 'truck.created', group: 'fleet' },
  { source: 'activity_log', kind: 'truck.driver_assigned', group: 'fleet' },
  { source: 'activity_log', kind: 'truck.driver_unassigned', group: 'fleet' },
  { source: 'activity_log', kind: 'truck.status_changed', group: 'fleet' },
  { source: 'activity_log', kind: 'truck.updated', group: 'fleet' },
  // 0284 (fleet-driver-pay): truck maintenance.
  { source: 'activity_log', kind: 'truck.maintenance_event_recorded', group: 'fleet' },
  { source: 'activity_log', kind: 'truck.maintenance_event_voided', group: 'fleet' },
  { source: 'activity_log', kind: 'truck.maintenance_plan_retired', group: 'fleet' },
  { source: 'activity_log', kind: 'truck.maintenance_plan_saved', group: 'fleet' },
  { source: 'activity_log', kind: 'truck.maintenance_tracking_set', group: 'fleet' },
  { source: 'activity_log', kind: 'truck.odometer_recorded', group: 'fleet' },
  { source: 'activity_log', kind: 'truck.odometer_voided', group: 'fleet' },
  { source: 'activity_log', kind: 'load.priced', group: 'load_changes' },
  { source: 'activity_log', kind: 'load.updated', group: 'load_changes' },
  { source: 'activity_log', kind: 'agency.fee_settings_updated', group: 'money' },
  { source: 'activity_log', kind: 'agency.pay_settings_updated', group: 'money' },
  { source: 'activity_log', kind: 'carrier.dispatch_plan_removed', group: 'money' },
  { source: 'activity_log', kind: 'carrier.dispatch_plan_set', group: 'money' },
  // 0279 (fleet-driver-pay): who pays the carrier's drivers.
  { source: 'activity_log', kind: 'carrier.driver_payer_set', group: 'money' },
  { source: 'activity_log', kind: 'carrier.invoice_settings_updated', group: 'money' },
  { source: 'activity_log', kind: 'dispatcher_pay.payment_recorded', group: 'money' },
  { source: 'activity_log', kind: 'dispatcher_pay.payment_reversed', group: 'money' },
  { source: 'activity_log', kind: 'dispatcher_pay.plan_set', group: 'money' },
  { source: 'activity_log', kind: 'dispatcher_pay.settings_set', group: 'money' },
  { source: 'activity_log', kind: 'driver.pay_plan_removed', group: 'money' },
  { source: 'activity_log', kind: 'driver.pay_plan_set', group: 'money' },
  // 0281 (fleet-driver-pay): hours of an hourly load, deductions and their catalog, weekly driver settlements.
  { source: 'activity_log', kind: 'load.driver_hours_set', group: 'money' },
  { source: 'activity_log', kind: 'driver.deduction_created', group: 'money' },
  { source: 'activity_log', kind: 'driver.deduction_updated', group: 'money' },
  { source: 'activity_log', kind: 'driver.deduction_retired', group: 'money' },
  { source: 'activity_log', kind: 'agency.deduction_template_saved', group: 'money' },
  { source: 'activity_log', kind: 'agency.deduction_template_retired', group: 'money' },
  { source: 'activity_log', kind: 'driver_settlement.generated', group: 'money' },
  { source: 'activity_log', kind: 'driver_settlement.line_added', group: 'money' },
  { source: 'activity_log', kind: 'driver_settlement.line_removed', group: 'money' },
  { source: 'activity_log', kind: 'driver_settlement.line_restored', group: 'money' },
  { source: 'activity_log', kind: 'driver_settlement.line_amount_set', group: 'money' },
  { source: 'activity_log', kind: 'driver_settlement.finalized', group: 'money' },
  { source: 'activity_log', kind: 'driver_settlement.canceled', group: 'money' },
  { source: 'activity_log', kind: 'driver_settlement.paid_marked', group: 'money' },
  { source: 'activity_log', kind: 'driver_settlement.paid_unmarked', group: 'money' },
  { source: 'activity_log', kind: 'invoice.charge_raised', group: 'money' },
  { source: 'activity_log', kind: 'invoice.email_requested', group: 'money' },
  { source: 'activity_log', kind: 'invoice.line_added', group: 'money' },
  { source: 'activity_log', kind: 'invoice.payment_recorded', group: 'money' },
  { source: 'activity_log', kind: 'invoice.payment_reversed', group: 'money' },
  { source: 'activity_log', kind: 'invoice.raised', group: 'money' },
  { source: 'activity_log', kind: 'invoice.written_off', group: 'money' },
  { source: 'activity_log', kind: 'load.owner_commission_cleared', group: 'money' },
  { source: 'activity_log', kind: 'load.owner_commission_set', group: 'money' },
  { source: 'activity_log', kind: 'owner_statement.canceled', group: 'money' },
  { source: 'activity_log', kind: 'owner_statement.finalized', group: 'money' },
  { source: 'activity_log', kind: 'owner_statement.generated', group: 'money' },
  { source: 'activity_log', kind: 'owner_statement.line_added', group: 'money' },
  { source: 'activity_log', kind: 'owner_statement.line_amount_set', group: 'money' },
  { source: 'activity_log', kind: 'owner_statement.line_removed', group: 'money' },
  { source: 'activity_log', kind: 'owner_statement.line_restored', group: 'money' },
  { source: 'activity_log', kind: 'truck_lease.created', group: 'money' },
  { source: 'activity_log', kind: 'truck_lease.ended', group: 'money' },
  { source: 'activity_log', kind: 'truck_lease.updated', group: 'money' },
  { source: 'activity_log', kind: 'truck.dispatch_plan_removed', group: 'money' },
  { source: 'activity_log', kind: 'truck.dispatch_plan_set', group: 'money' },
  { source: 'activity_log', kind: 'receivable.dispute_cleared', group: 'receivables' },
  { source: 'activity_log', kind: 'receivable.disputed', group: 'receivables' },
  { source: 'activity_log', kind: 'receivable.invoice_emailed', group: 'receivables' },
  { source: 'activity_log', kind: 'receivable.invoiced', group: 'receivables' },
  { source: 'activity_log', kind: 'receivable.payment_recorded', group: 'receivables' },
  { source: 'activity_log', kind: 'receivable.payment_reversed', group: 'receivables' },
  { source: 'activity_log', kind: 'receivable.reopened', group: 'receivables' },
  { source: 'activity_log', kind: 'receivable.unmarked', group: 'receivables' },
  { source: 'activity_log', kind: 'receivable.updated', group: 'receivables' },
  { source: 'activity_log', kind: 'dispatcher.carrier_assigned', group: 'team' },
  { source: 'activity_log', kind: 'dispatcher.carrier_unassigned', group: 'team' },
  { source: 'activity_log', kind: 'member.invite_declined', group: 'team' },
  { source: 'activity_log', kind: 'member.invite_resent', group: 'team' },
  { source: 'activity_log', kind: 'member.invite_revoked', group: 'team' },
  { source: 'activity_log', kind: 'member.invited', group: 'team' },
  { source: 'activity_log', kind: 'member.joined', group: 'team' },
  { source: 'activity_log', kind: 'member.role_changed', group: 'team' },
  { source: 'activity_log', kind: 'member.status_changed', group: 'team' },
  { source: 'activity_log', kind: 'permission.changed', group: 'team' },
];

const GROUP_OF = new Map(ACTIVITY_KINDS.map((k) => [`${k.source}:${k.kind}`, k.group]));
const KNOWN = new Set(GROUP_OF.keys());

export function groupOf(source: string, kind: string): ActivityGroupOrOther {
  return GROUP_OF.get(`${source}:${kind}`) ?? 'other';
}

/** The groups a reader may filter by or mute (design 5): office groups need activity.office, money needs activity.money. */
export function visibleGroups({ office, money }: { office: boolean; money: boolean }): ActivityGroup[] {
  return ACTIVITY_GROUPS.filter((g) => (g === 'money' ? money : OFFICE_GROUPS.includes(g) ? office : true));
}

export interface ActivityItem {
  id: string;
  source: ActivitySource;
  kind: string;
  group: ActivityGroupOrOther;
  at: string;
  unread: boolean;
  actorUserId: string | null;
  actorName: string | null;
  /** member | driver | operator | system */
  actorKind: string | null;
  actorRole: string | null;
  onBehalfUserId: string | null;
  onBehalfName: string | null;
  loadId: string | null;
  loadNumber: string | null;
  dispatcherUserId: string | null;
  dispatcherName: string | null;
  driverId: string | null;
  driverName: string | null;
  carrierId: string | null;
  carrierName: string | null;
  entityType: string | null;
  entityId: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  /** The server's allowlisted detail (channel, seq, action, kind, fields, status, role). Never an amount. */
  detail: Record<string, unknown>;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

export function toActivityItem(r: Record<string, unknown>): ActivityItem | null {
  const source = r.source;
  if (source !== 'load_events' && source !== 'activity_log') return null;
  const id = str(r.id);
  const kind = str(r.kind);
  const at = str(r.at);
  if (!id || !kind || !at) return null;
  const group = str(r.kind_group);
  return {
    id,
    source,
    kind,
    group: group && (ACTIVITY_GROUPS as readonly string[]).includes(group) ? (group as ActivityGroup) : groupOf(source, kind),
    at,
    unread: r.unread === true,
    actorUserId: str(r.actor_user_id),
    actorName: str(r.actor_name),
    actorKind: str(r.actor_kind),
    actorRole: str(r.actor_role),
    onBehalfUserId: str(r.on_behalf_user_id),
    onBehalfName: str(r.on_behalf_name),
    loadId: str(r.load_id),
    loadNumber: str(r.load_number),
    dispatcherUserId: str(r.dispatcher_user_id),
    dispatcherName: str(r.dispatcher_name),
    driverId: str(r.driver_id),
    driverName: str(r.driver_name),
    carrierId: str(r.carrier_id),
    carrierName: str(r.carrier_name),
    entityType: str(r.entity_type),
    entityId: str(r.entity_id),
    fromStatus: str(r.from_status),
    toStatus: str(r.to_status),
    detail: typeof r.detail === 'object' && r.detail !== null && !Array.isArray(r.detail) ? (r.detail as Record<string, unknown>) : {},
  };
}

/** The copy key of a kind: `activity.kinds.<source>.<kind>` (an action's dot nests: `driver.updated` is
 *  `activity_log.driver.updated`); unknown -> `activity.kinds.other`. Nested, not flattened with "_": the
 *  locale files ship in the operator bundle too, and `broker_created` would trip the bundle-split marker
 *  `broker_create` (web/scripts/check-bundle-split.mjs). */
export function labelKey({ source, kind }: { source: string; kind: string }): string {
  if (!KNOWN.has(`${source}:${kind}`)) return 'activity.kinds.other';
  return `activity.kinds.${source}.${kind}`;
}

const STOP_KINDS = new Set(['pickup', 'delivery']);

/** The row's line in the UI language. A transition names the status it moved to, the same reading as
 *  the load timeline (lib/loadTimeline.ts): dispatched -> booked is the driver's decline unless the office
 *  took the load back (detail.action 'unassigned'). */
export function activityLabel(item: ActivityItem, t: TFunction): string {
  if (item.source === 'load_events') {
    switch (item.kind) {
      case 'transition': {
        if (item.detail.action === 'unassigned') return t('activity.kinds.load_events.unassigned');
        if (item.fromStatus === 'dispatched' && item.toStatus === 'booked') return t('activity.kinds.load_events.declined');
        const status = fromDb(item.toStatus);
        return t('activity.kinds.load_events.transition', { status: t(`loadStatus.${status ?? 'unknown'}`) });
      }
      case 'stop_arrived':
      case 'stop_departed':
      case 'stop_added':
      case 'stop_removed':
      case 'stop_rescheduled': {
        const k = typeof item.detail.kind === 'string' && STOP_KINDS.has(item.detail.kind) ? item.detail.kind : 'any';
        return t(labelKey(item), { stop: t(`activity.stop.${k}`) });
      }
      default:
        break;
    }
  }
  return t(labelKey(item));
}

export interface ActivityLinks {
  load?: string;
  driver?: string;
  dispatcher?: string;
}

/**
 * Where a row's names lead (design 9.2): the load page, the driver's sheet, and the dispatcher's roster
 * card. The dispatcher is the load's dispatcher when there is one, else the actor; the link exists only
 * when that person's CURRENT role is dispatcher (nobody else has a roster card).
 */
export function rowLinks(item: ActivityItem, roleOf: (userId: string) => string | null): ActivityLinks {
  const out: ActivityLinks = {};
  if (item.loadId) out.load = `/loads/${item.loadId}`;
  if (item.driverId) out.driver = `/carriers/drivers?driver=${encodeURIComponent(item.driverId)}`;
  const who = item.dispatcherUserId ?? (item.actorKind === 'member' || item.actorRole ? item.actorUserId : null);
  if (who) {
    const role = roleOf(who) ?? (who === item.actorUserId ? item.actorRole : null);
    if (role === 'dispatcher') out.dispatcher = `/dispatchers/assigned-drivers#roster-${who}`;
  }
  return out;
}
